import { Popover } from "@headlessui/react";
import { CheckIcon } from "@heroicons/react/16/solid";
import { ChevronDownIcon, ChevronRightIcon, EllipsisVerticalIcon } from "@heroicons/react/24/outline";
import React from "react";
import { twMerge } from "tailwind-merge";

import { Label } from "@fronterahealth/frontera-ui-components";

import { ReportGoalType } from "@api/graphql/types-and-hooks";
import { ContextMenu, ContextMenuContent, ContextMenuTrigger } from "@components/ContextMenu";
import { HighlightedText } from "@components/HighlightedText/HighlightedText";
import { GoalActionsMenu } from "@pages/ClinicalReportDetails/ReportPage/Components/GoalActionsMenu";
import { GoalBadge } from "@pages/ClinicalReportDetails/ReportPage/Components/GoalBadge";

interface GoalLineRowProps {
  goal: ReportGoalType;
  isSelected: boolean;
  expanded?: boolean;
  className?: string;
  onToggle?: () => void;
  onGoalClick: (goal: ReportGoalType) => void;
  /** Term to highlight in the goal name */
  highlightTerm?: string;
  /** Whether this goal is in an unapproved-only view */
  isGhostGoal?: boolean;
  /** Whether to show the expand/collapse chevron */
  hasTargets?: boolean;
  isRefreshingProgramming?: boolean;
  /** Optional identifier prefix (e.g. "1. ") rendered before the name. */
  identifierPrefix?: string;
}

export const GoalLineRow: React.FC<GoalLineRowProps> = ({
  goal,
  isSelected,
  expanded = true,
  className,
  onToggle,
  onGoalClick,
  highlightTerm,
  isGhostGoal = false,
  hasTargets = false,
  isRefreshingProgramming = false,
  identifierPrefix,
}) => {
  const handleGoalClick = () => {
    onGoalClick(goal);
  };

  return (
    <ContextMenu>
      <ContextMenuTrigger disabled={isRefreshingProgramming && !!goal.summaryReviewed}>
        <div
          className={twMerge(
            "group flex w-full items-center gap-2 rounded-sm py-0.5 pr-2",
            isSelected ? "bg-limestone-200 hover:bg-limestone-200" : "hover:bg-limestone-50",
            className,
          )}
        >
          <div className="flex min-w-0 flex-1 items-center">
            {!highlightTerm && (
              <button
                type="button"
                className="shrink-0 rounded-sm p-2"
                onClick={() => {
                  onToggle?.();
                }}
              >
                {hasTargets ? (
                  expanded ? (
                    <ChevronDownIcon className="h-3 w-3 text-icon-secondary" />
                  ) : (
                    <ChevronRightIcon className="h-3 w-3 text-icon-secondary" />
                  )
                ) : (
                  <div className="h-3 w-3" />
                )}
              </button>
            )}
            <Label
              size="small"
              displayType={isSelected ? "loud" : "normal"}
              className={twMerge(
                `min-w-0 flex-1 truncate text-left ph-no-capture`,
                isGhostGoal ? "opacity-30 pointer-events-none" : "",
              )}
              title={goal.goalName}
              colorType="primary"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleGoalClick();
              }}
            >
              {identifierPrefix && <span className="text-text-secondary">{identifierPrefix}</span>}
              <HighlightedText text={goal.goalName || "Untitled Goal"} highlightTerm={highlightTerm} />
            </Label>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <div className={twMerge("flex items-center gap-2", isGhostGoal ? "opacity-30" : "")}>
              {!(isRefreshingProgramming && goal.summaryReviewed) && (
                <Popover className="relative">
                  {({ close, open }) => (
                    <>
                      <Popover.Button
                        className={twMerge(
                          "flex items-center justify-center w-8 h-6 rounded-sm hover:bg-limestone-200 transition-colors focus:outline-hidden",
                          "hidden group-hover:flex",
                          open && "flex",
                        )}
                      >
                        <EllipsisVerticalIcon className="w-4 h-4 text-icon-secondary" strokeWidth={2} />
                      </Popover.Button>
                      <Popover.Panel className="absolute right-0 z-10 mt-1 w-56 rounded-lg bg-white shadow-lg ring-1 ring-black/5 focus:outline-hidden">
                        <GoalActionsMenu goal={goal} close={close} />
                      </Popover.Panel>
                    </>
                  )}
                </Popover>
              )}
              <GoalBadge status={goal.status} />
            </div>
            <CheckIcon
              className={twMerge(
                "h-4 w-4",
                goal.summaryReviewed ? "text-green-700 opacity-100!" : "text-limestone-400",
              )}
            />
          </div>
        </div>
      </ContextMenuTrigger>
      <ContextMenuContent>{({ close }) => <GoalActionsMenu goal={goal} close={close} />}</ContextMenuContent>
    </ContextMenu>
  );
};
